import { useEffect, useRef, useState } from "react";
import { playClipSilently, speakInsteadOfClip } from "../lib/clipVoice";
import type { BrowserConfig, BrowserPageConfig } from "../scenario/types";
import { PhishingLogin } from "./PhishingLogin";
import { WindowTitlebar } from "./WindowTitlebar";
import { audio, duckSceneAudio } from "../lib/audio";
import { tx, txr } from "../lib/tx";

/**
 * Simulated Chrome. Two flows:
 *   - "navigate"/"login": the learner types the target URL (guided), Enter loads
 *     the spoofed login page, and submitting credentials fires onSubmit.
 *   - "hijack" (WFH session-hijack): an SSL/certificate-warning interstitial
 *     ("Your connection is not private") -> Proceed anyway (onProceedInsecure,
 *     records ignored_cert_warning) -> login -> a "session hijacked" disconnect
 *     screen (shown to the victim as a benign network failure) -> Continue.
 */
export function Browser({
  config,
  onNavigate,
  onSubmit,
  phase,
  onProceedInsecure,
  onHijackContinue,
  onDownload,
  onBackToSafety,
  onAware,
  onAwarenessDone,
}: {
  config: BrowserConfig;
  onNavigate: (url: string) => void;
  onSubmit: (fields: { email: string; password: string }) => void;
  phase: "navigate" | "login" | "hijack" | "landing" | "page" | "awareness";
  /** fired when the learner clicks through the certificate warning. */
  onProceedInsecure?: () => void;
  /** fired when the learner dismisses the session-hijack screen (advances). */
  onHijackContinue?: () => void;
  /** fired when the learner downloads the malicious document on the landing. */
  onDownload?: () => void;
  /** when set, "Back to safety" on the certificate warning is a real choice. */
  onBackToSafety?: () => void;
  onAware?: (stage: "poster" | "playing" | "ended") => void;
  onAwarenessDone?: () => void;
}) {
  const startUrl =
    phase === "hijack"
      ? config.certWarning?.url ?? config.homeUrl
      : phase === "landing"
      ? config.landing?.url ?? config.targetUrl
      : config.homeUrl;
  const [url, setUrl] = useState(startUrl);
  const [loaded, setLoaded] = useState(phase === "login");
  const [focus, setFocus] = useState(false);
  // hijack sub-flow: which screen of the WFH compromise is showing.
  const [hjStage, setHjStage] = useState<"cert" | "login" | "hijacked">("cert");
  const videoRef = useRef<HTMLVideoElement>(null);
  const awareRef = useRef<HTMLVideoElement>(null);
  const ducked = useRef(false);
  const [filmOn, setFilmOn] = useState(false);

  // Landing: play the owner-congrats clip (carries audio); else TTS the line.
  useEffect(() => {
    if (phase !== "landing") return;
    const ld = config.landing;
    if (!ld) return;
    if (ld.speakText && (!ld.clipUrl || speakInsteadOfClip()))
      void audio.prefetchSpeech(ld.speakText, { voice: ld.voiceId || "ash", lang: ld.language || "en-US" });
    if (ld.clipUrl && videoRef.current && !speakInsteadOfClip()) {
      const v = videoRef.current;
      v.muted = !audio.isEnabled();
      v.play().catch(() => {
        if (ld.speakText) audio.speak(ld.speakText, { voice: ld.voiceId || "ash", lang: ld.language || "en-US" });
      });
    } else if (ld.speakText) {
      if (ld.clipUrl) playClipSilently(videoRef.current, ld.clipUrl);
      const t = window.setTimeout(
        () => audio.speak(ld.speakText!, { voice: ld.voiceId || "ash", lang: ld.language || "en-US" }),
        600
      );
      return () => {
        audio.cancelSpeech();
        window.clearTimeout(t);
      };
    }
    return () => audio.cancelSpeech();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  useEffect(() => {
    if (phase !== "awareness") return;
    setFilmOn(false);
    onAware?.("poster");
    return () => {
      awareRef.current?.pause();
      if (ducked.current) {
        ducked.current = false;
        duckSceneAudio(1);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const submit = () => {
    if (url.includes(config.targetUrl)) {
      audio.ui("pageOpen");
      setLoaded(true);
      onNavigate(url);
    }
  };

  if (phase === "awareness" && config.awarenessVideo) {
    const film = config.awarenessVideo;
    const playLabel = film.playLabel ?? "Watch the video";
    const continueLabel = film.continueLabel ?? "Continue to quiz";
    const startFilm = () => {
      const v = awareRef.current;
      if (!v) return;
      v.muted = !audio.isEnabled();
      ducked.current = true;
      duckSceneAudio(0.2);
      void v.play();
      setFilmOn(true);
      onAware?.("playing");
    };
    return (
      <div className={`browser winapp${filmOn ? " is-film" : ""}`}>
        <WindowTitlebar title={tx("Chrome")} />
        <div className="chrome">
          <div className="nav">
            <span>{"\u2190"}</span>
            <span>{"\u2192"}</span>
            <span>{"\u21BB"}</span>
          </div>
          <div className="urlbar">
            <span className="lock">{"\u{1F512}"}</span>
            <input value={config.homeUrl} readOnly aria-label={tx("Address bar")} />
          </div>
        </div>
        <div className="page aware-page">
          <div className={`aware${filmOn ? " is-film" : ""}`}>
            <div className="aware-top">
              {film.brand && <div className="aware-kicker">{film.brand}</div>}
              <h1>{film.headline}</h1>
              <p>{film.body}</p>
            </div>
            <div className="aware-stage">
              <video
                ref={awareRef}
                className={filmOn ? "aware-vid" : "aware-vid wait"}
                src={film.url}
                playsInline
                controls={filmOn}
                onEnded={() => {
                  onAware?.("ended");
                  if (ducked.current) {
                    ducked.current = false;
                    duckSceneAudio(1);
                  }
                }}
              />
              {!filmOn && (
                <button type="button" className="aware-play cf-blink-strong" aria-label={playLabel} onClick={startFilm}>
                  <span className="aware-mark" aria-hidden="true">{"\u25B6"}</span>
                  {playLabel}
                </button>
              )}
            </div>
            {filmOn && (
              <div className="aware-foot">
                <button type="button" className="aware-quiz cf-blink-strong" onClick={() => onAwarenessDone?.()}>
                  {continueLabel}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // --- prize / congratulations landing (lure final hop) -----------------------
  if (phase === "landing" && config.landing) {
    const ld = config.landing;
    return (
      <div className="browser winapp">
        <WindowTitlebar title={tx("Chrome")} />
        <div className="chrome">
          <div className="nav">
            <span>{"\u2190"}</span>
            <span>{"\u2192"}</span>
            <span>{"\u21BB"}</span>
          </div>
          <div className="urlbar">
            <span className="lock">{"\u{1F512}"}</span>
            <input value={url} onChange={(e) => setUrl(e.target.value)} aria-label={tx("Address bar")} readOnly />
          </div>
        </div>
        <div className="page">
          <div className="prize">
            <div className="prize-conf" aria-hidden>{"\u{1F389}"}</div>
            {ld.brand && <div className="prize-brand">{ld.brand}</div>}
            <h1 className="prize-headline">{ld.headline}</h1>
            <div className="prize-video">
              {ld.clipUrl ? (
                <video ref={videoRef} className="prize-vid" src={ld.clipUrl} poster={ld.posterUrl} playsInline controls={false} />
              ) : (
                <div className="prize-vid-ph">
                  {ld.posterUrl ? <img src={ld.posterUrl} alt="" /> : <span className="prize-play">{"\u25B6"}</span>}
                </div>
              )}
            </div>
            {ld.body && <p className="prize-body">{ld.body}</p>}
            <button type="button" className="btn prize-download blink" onClick={() => onDownload?.()}>
              {"\u2B07"} {ld.downloadLabel ?? tx("Download {v}", { v: ld.fileName ?? "claim form" })}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // --- structured content page (article / signin / portal / profile) ----------
  // Renders a real web page INSIDE the same Chrome chrome (used by courses that
  // want a news article / company portal / social profile / plain sign-in
  // without inventing a bespoke widget). Additive: only active when the caller
  // passes phase="page" + config.page, so existing flows are untouched.
  if (phase === "page" && config.page) {
    const pg = config.page;
    return (
      <div className="browser winapp">
        <WindowTitlebar title={tx("Chrome")} />
        <div className="chrome">
          <div className="nav">
            <span>{"\u2190"}</span>
            <span>{"\u2192"}</span>
            <span>{"\u21BB"}</span>
          </div>
          <div className="urlbar">
            <span className="lock">{"\u{1F512}"}</span>
            <input value={pg.url} readOnly aria-label={tx("Address bar")} />
          </div>
        </div>
        <div className="page">
          {pg.kind === "article" && (
            <article className="bpage bpage-article">
              {pg.brand && <div className="bpage-brand">{pg.brand}</div>}
              {pg.headline && <h1 className="bpage-head">{pg.headline}</h1>}
              {pg.dek && <p className="bpage-dek">{pg.dek}</p>}
              {(pg.paragraphs ?? []).map((p, i) => (
                <p key={i} className="bpage-p">{p}</p>
              ))}
              {pg.record && (
                <div className="bpage-record">
                  {pg.record.title && <div className="bpage-record-title">{pg.record.title}</div>}
                  <dl>
                    {pg.record.rows.map((r, i) => (
                      <div key={i}>
                        <dt>{r.label}</dt>
                        <dd className={r.secret ? "bpage-secret" : ""}>{r.value}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}
              {pg.cta && (
                <button type="button" className="btn bpage-cta blink" onClick={() => onNavigate(pg.cta!.key)}>
                  {pg.cta.label}
                </button>
              )}
            </article>
          )}

          {pg.kind === "signin" && <BrowserSignin page={pg} onSubmit={onSubmit} />}

          {pg.kind === "portal" && (
            <div className="bpage bpage-portal">
              {pg.brand && <div className="bpage-portal-top">{pg.brand}</div>}
              {pg.headline && <h1 className="bpage-head">{pg.headline}</h1>}
              {pg.note && <div className="bpage-note">{pg.note}</div>}
              {(pg.paragraphs ?? []).map((p, i) => (
                <p key={i} className="bpage-p">{p}</p>
              ))}
              {pg.cta && (
                <button type="button" className="btn bpage-cta" onClick={() => onNavigate(pg.cta!.key)}>
                  {pg.cta.label}
                </button>
              )}
            </div>
          )}

          {pg.kind === "profile" && (
            <div className="bpage bpage-profile">
              <div className="bpage-profile-head">
                <span className="bpage-profile-av" aria-hidden>{(pg.brand || pg.headline || "?").slice(0, 1)}</span>
                <div>
                  <h1 className="bpage-head">{pg.headline || pg.brand}</h1>
                  {pg.brand && pg.headline && <span className="bpage-profile-handle">{pg.brand}</span>}
                </div>
              </div>
              <div className="bpage-facts">
                {(pg.facts ?? []).map((f, i) => (
                  <div key={i} className="bpage-fact">
                    <span>{f.label}</span>
                    <strong>{f.value}</strong>
                  </div>
                ))}
              </div>
              {(pg.paragraphs ?? []).map((p, i) => (
                <p key={i} className="bpage-p">{p}</p>
              ))}
              {pg.cta && (
                <button type="button" className="btn bpage-cta blink" onClick={() => onNavigate(pg.cta!.key)}>
                  {pg.cta.label}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // --- WFH session-hijack flow -------------------------------------------------
  if (phase === "hijack") {
    const cw = config.certWarning;
    const insecure = hjStage === "cert";
    return (
      <div className="browser winapp">
        <WindowTitlebar title={tx("Chrome")} />
        <div className="chrome">
          <div className="nav">
            <span>{"\u2190"}</span>
            <span>{"\u2192"}</span>
            <span>{"\u21BB"}</span>
          </div>
          <div className={`urlbar ${insecure ? "insecure" : ""}`}>
            <span className="lock">{insecure ? "\u26A0" : "\u{1F512}"}</span>
            <input value={url} onChange={(e) => setUrl(e.target.value)} aria-label={tx("Address bar")} readOnly />
          </div>
        </div>
        <div className="page">
          {hjStage === "cert" && (
            <div className="certwarn">
              <div className="certwarn-card">
                <div className="certwarn-ico" aria-hidden>{"\u26A0"}</div>
                <h2 className="certwarn-title">{cw?.title ?? tx("Your connection is not private")}</h2>
                <p className="certwarn-body">
                  {cw?.body ??
                    tx("Attackers might be trying to steal your information from this site (for example, passwords, messages or credit cards).")}
                </p>
                {cw?.errorCode && <div className="certwarn-code">{cw.errorCode}</div>}
                <div className="certwarn-actions">
                  {onBackToSafety && (
                    <button type="button" className="certwarn-back" onClick={() => onBackToSafety()}>
                      {cw?.backLabel ?? tx("Back to safety")}
                    </button>
                  )}
                  <button
                    type="button"
                    className={`certwarn-proceed${onBackToSafety ? "" : " blink"}`}
                    onClick={() => {
                      onProceedInsecure?.();
                      setHjStage("login");
                    }}
                  >
                    {cw?.proceedLabel ?? tx("Proceed anyway (unsafe)")}
                  </button>
                </div>
                {!onBackToSafety && <div className="certwarn-hint" role="status">
                  <span className="certwarn-hint-ico" aria-hidden>{"\u27A4"}</span>
                  <span>
                    {txr("You need to reach the portal — click <0>\"{v}\"</0> to continue.", { v: cw?.proceedLabel ?? tx("Proceed anyway (unsafe)") }, [<strong />])}
                  </span>
                </div>}
              </div>
            </div>
          )}

          {hjStage === "login" && config.phishing && (
            <PhishingLogin
              config={config.phishing}
              onSubmit={(fields) => {
                onSubmit(fields);
                setHjStage("hijacked");
              }}
            />
          )}

          {hjStage === "hijacked" && (
            <div className="hijack">
              <div className="hijack-card">
                <div className="hijack-spinner" aria-hidden />
                <h2 className="hijack-title">{config.hijack?.title ?? tx("Temporary network failure")}</h2>
                <p className="hijack-body">
                  {config.hijack?.body ??
                    tx("You have been disconnected. Please wait while we restore your session…")}
                </p>
                {config.hijack?.attackerNote && (
                  <div className="hijack-attacker">
                    <span className="hijack-attacker-dot" />
                    {config.hijack.attackerNote}
                  </div>
                )}
                <button type="button" className="btn hijack-continue" onClick={() => onHijackContinue?.()}>
                  {config.hijack?.continueLabel ?? tx("What just happened?")}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // --- standard navigate / login ----------------------------------------------
  return (
    <div className="browser winapp">
      <WindowTitlebar title={tx("Chrome")} />
      <div className="chrome">
        <div className="nav">
          <span>{"\u2190"}</span>
          <span>{"\u2192"}</span>
          <span>{"\u21BB"}</span>
        </div>
        <div className={`urlbar ${focus ? "focus" : ""}`}>
          <span className="lock">{loaded ? "\u{1F512}" : ""}</span>
          <input
            value={url}
            placeholder={tx("Search Google or type a URL")}
            onFocus={() => setFocus(true)}
            onBlur={() => setFocus(false)}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") submit();
            }}
            aria-label={tx("Address bar")}
          />
        </div>
      </div>
      <div className="page">
        {loaded && config.loadsPage === "phishing_login" && config.phishing ? (
          <PhishingLogin config={config.phishing} onSubmit={onSubmit} />
        ) : (
          <div
            style={{
              height: "100%",
              display: "grid",
              placeItems: "center",
              color: "#5b6670",
              fontSize: 64,
              fontWeight: 700,
            }}
          >
            {tx("Google")}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * A plain, legitimate sign-in page rendered inside the browser chrome. Reports
 * every submit up via onSubmit so the HOST decides success/failure (e.g. a
 * course that escalates coaching across wrong attempts). `page.prefill`, when
 * set by the host, fills the password field (the coach's "place it for me").
 * All coaching stays OFF this page — only a neutral `page.error` may show.
 */
function BrowserSignin({
  page,
  onSubmit,
}: {
  page: BrowserPageConfig;
  onSubmit: (fields: { email: string; password: string }) => void;
}) {
  const [password, setPassword] = useState(page.prefill ?? "");
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (page.prefill) setPassword(page.prefill);
  }, [page.prefill]);
  const email = page.email ?? "";
  return (
    <div className="bsignin">
      <form
        className="bsignin-card"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit({ email, password });
        }}
        autoComplete="off"
      >
        {page.brand && <div className="bsignin-brand">{page.brand}</div>}
        <h2>{page.headline ?? tx("Sign in")}</h2>
        <label>
          {tx("Email")}
          <input value={email} readOnly />
        </label>
        <label>
          {txr("Password{span}", { span: <span className="bsignin-pw">
            <input
              className={shown ? "" : "bsignin-mask"}
              type="text"
              name="bank-guess"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              data-1p-ignore="true"
              data-lpignore="true"
              value={password}
              placeholder={tx("Enter your password")}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button type="button" className="bsignin-reveal" onClick={() => setShown((v) => !v)}>
              {shown ? tx("Hide") : tx("Show")}
            </button>
          </span> })}
        </label>
        {page.error && <p className="bsignin-error">{page.error}</p>}
        <button type="submit" className="btn bsignin-submit">
          {tx("Sign in")}
        </button>
      </form>
    </div>
  );
}
